/**
 * Custom hooks for offline functionality
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'

import { drillTimerApi, isNetworkError } from '../services/drillTimerService'
import { db } from '../utils/db'

/**
 * Hook to track online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}

/**
 * Hook to get count of pending reports
 */
export function usePendingReports() {
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    const updateCount = async () => {
      try {
        const count = await db.pendingReports.where('status').equals('pending').count()
        setPendingCount(count)
      } catch (error) {
        // Silent fail - count will remain at previous value
      }
    }

    updateCount()

    // Poll every 5 seconds
    const interval = setInterval(updateCount, 5000)
    return () => clearInterval(interval)
  }, [])

  return pendingCount
}

/**
 * Hook to submit drill report (with offline support)
 * @param {string} entityId - The entity ID for the drill report
 */
export function useSubmitDrillReport(entityId) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (report) => {
      // Check online status at execution time, not from closure
      const isCurrentlyOnline = navigator.onLine

      // If offline, queue the report with entityId
      if (!isCurrentlyOnline) {
        await db.pendingReports.add({
          reportData: report,
          entityId,
          queuedAt: new Date(),
          status: 'pending',
          retryCount: 0,
        })
        // Return success with offline flag instead of throwing
        return { success: true, offline: true }
      }

      // Try to submit online - entityId is now required
      try {
        const result = await drillTimerApi.createLog(entityId, report)
        return { success: true, offline: false, data: result }
      } catch (error) {
        // Network error while thinking we're online - queue it
        if (isNetworkError(error)) {
          await db.pendingReports.add({
            reportData: report,
            entityId,
            queuedAt: new Date(),
            status: 'pending',
            retryCount: 0,
          })
          return { success: true, offline: true }
        }
        // Other errors - re-throw
        throw error
      }
    },
    onSuccess: (result) => {
      if (result.offline) {
        toast.success('Report saved offline. Will sync when connection is restored.', {
          duration: 5000,
          icon: '📴',
        })
      } else {
        toast.success('Report submitted successfully!')
      }
      queryClient.invalidateQueries({ queryKey: ['drillLogs', entityId] })
    },
    onError: (error) => {
      toast.error(`Failed to submit report: ${error.response?.data?.error || error.message}`)
    },
  })

  return mutation
}

/**
 * Hook to sync queued reports
 */
export function useSyncQueue() {
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()
  const [isSyncing, setIsSyncing] = useState(false)

  const syncQueue = useCallback(async () => {
    if (!isOnline) return

    try {
      setIsSyncing((current) => {
        if (current) return current // Already syncing
        return true
      })

      const pending = await db.pendingReports.where('status').equals('pending').toArray()

      if (pending.length === 0) {
        return
      }

      toast.loading(`Syncing ${pending.length} report(s)...`, { id: 'sync' })

      // Group pending reports by entityId
      const reportsByEntity = pending.reduce((acc, report) => {
        const eid = report.entityId
        if (!eid) {
          // Skip reports without entityId (legacy data)
          return acc
        }
        if (!acc[eid]) {
          acc[eid] = []
        }
        acc[eid].push(report)
        return acc
      }, {})

      let totalSuccess = 0
      let totalFailed = 0

      // Sync each entity's reports separately
      // eslint-disable-next-line no-restricted-syntax
      for (const [entityId, entityReports] of Object.entries(reportsByEntity)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await drillTimerApi.bulkCreateLogs(
            entityId,
            entityReports.map((p) => p.reportData),
          )

          // Remove successfully synced reports
          if (result.created) {
            // eslint-disable-next-line no-restricted-syntax
            for (const createdLog of result.created) {
              const pendingReport = entityReports.find(
                (p) => p.reportData.name === createdLog.name,
              )
              if (pendingReport?.id) {
                // eslint-disable-next-line no-await-in-loop
                await db.pendingReports.delete(pendingReport.id)
              }
            }
            totalSuccess += result.created.length
          }

          // Mark failed reports
          if (result.errors) {
            // eslint-disable-next-line no-restricted-syntax
            for (const error of result.errors) {
              const pendingReport = entityReports.find((p) => p.reportData.name === error.log)
              if (pendingReport?.id) {
                // eslint-disable-next-line no-await-in-loop
                await db.pendingReports.update(pendingReport.id, {
                  status: 'failed',
                  error: JSON.stringify(error.errors || error.error),
                  retryCount: pendingReport.retryCount + 1,
                })
              }
            }
            totalFailed += result.errors.length
          }

          // eslint-disable-next-line no-await-in-loop
          queryClient.invalidateQueries({ queryKey: ['drillLogs', entityId] })
        } catch (error) {
          // Mark all reports for this entity as failed
          // eslint-disable-next-line no-restricted-syntax
          for (const report of entityReports) {
            if (report.id) {
              // eslint-disable-next-line no-await-in-loop
              await db.pendingReports.update(report.id, {
                status: 'failed',
                error: error.message,
                retryCount: report.retryCount + 1,
              })
            }
          }
          totalFailed += entityReports.length
        }
      }

      if (totalSuccess > 0) {
        toast.success(`Synced ${totalSuccess} report(s)!`, { id: 'sync' })
      }

      if (totalFailed > 0) {
        toast.error(`${totalFailed} report(s) failed to sync`, {
          duration: 5000,
        })
      }
    } catch (error) {
      toast.error('Failed to sync reports', { id: 'sync' })
    } finally {
      setIsSyncing(false)
    }
  }, [isOnline, queryClient])

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline) {
      syncQueue()
    }
  }, [isOnline, syncQueue])

  // Periodic sync every minute when online
  useEffect(() => {
    if (!isOnline) return undefined

    const interval = setInterval(() => {
      syncQueue()
    }, 60000) // 1 minute

    return () => clearInterval(interval)
  }, [isOnline, syncQueue])

  return { syncQueue, isSyncing }
}

/**
 * Hook to manually retry failed reports
 */
export function useRetryFailedReports() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const failed = await db.pendingReports.where('status').equals('failed').toArray()

      if (failed.length === 0) {
        throw new Error('No failed reports to retry')
      }

      // Reset their status to pending
      // eslint-disable-next-line no-restricted-syntax
      for (const report of failed) {
        // eslint-disable-next-line no-await-in-loop
        await db.pendingReports.update(report.id, {
          status: 'pending',
          error: undefined,
        })
      }

      return failed.length
    },
    onSuccess: (count) => {
      toast.success(`${count} report(s) queued for retry`)
      queryClient.invalidateQueries({ queryKey: ['pending-reports'] })
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}
